package org.teddapp.project.ui.card

import androidx.compose.foundation.Canvas
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.layer.GraphicsLayer
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.teddapp.project.card.BusinessCard
import org.teddapp.project.card.QrCode
import org.teddapp.project.card.TeddShare
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddScreenHeader
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer

/**
 * Carte de visite : un aperçu à montrer ou à faire scanner, et les champs qui la
 * composent. On modifie un brouillon, que l'aperçu et les partages suivent aussitôt ;
 * « Enregistrer » l'écrit dans le profil synchronisé, « Annuler » revient à la carte
 * enregistrée. Une carte arrivée d'un autre appareil n'écrase jamais un brouillon.
 */
@Composable
fun CardScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val settings = LocalAppSettings.current
    val scope = rememberCoroutineScope()
    val snackbar = remember { TeddSnackbarState() }
    val drawer = rememberSettingsDrawer()
    // La carte vit dans le profil : une modification faite sur un autre appareil arrive ici par la synchronisation.
    val stored = remember(settings.bio) { BusinessCard.decode(settings.bio) }
    // Le brouillon : nul tant qu'on n'a rien modifié, l'aperçu montre alors la carte enregistrée.
    var draft by remember { mutableStateOf<BusinessCard?>(null) }
    val dirty = draft != null && draft != stored
    // La carte enregistrée a changé (un autre appareil) pendant qu'un brouillon était ouvert.
    var newerArrived by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        if (stored.isEmpty) {
            // Première ouverture : la carte de l'appareil si elle existe (migration immédiate), sinon le nom du profil.
            val legacy = BusinessCard.loadLegacy(repos.preferences)
            if (legacy.isEmpty) draft = BusinessCard(name = settings.fullname) else settings.applyBio(BusinessCard.encode(legacy))
        }
    }
    LaunchedEffect(stored) {
        // Sans brouillon en cours, l'aperçu suit le profil ; avec, on prévient sans rien écraser.
        if (!dirty) draft = null else newerArrived = true
    }
    val card: BusinessCard? = draft ?: stored.takeUnless { it.isEmpty }
    val update: (BusinessCard) -> Unit = { next -> draft = next }
    val save: () -> Unit = {
        draft?.let { settings.applyBio(if (it.isEmpty) "" else BusinessCard.encode(it)) }
        draft = null
        newerArrived = false
        snackbar.show("Carte enregistrée")
    }
    val cancel: () -> Unit = {
        draft = null
        newerArrived = false
    }

    val body: @Composable (Modifier, Boolean) -> Unit = { bodyModifier, compact ->
        Box(modifier = bodyModifier) {
            card?.let { current -> CardContent(current, update, dirty, newerArrived, save, cancel, snackbar, compact) }
            TeddSnackbarHost(snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 28.dp))
        }
    }
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        if (wc == WindowClass.Compact) {
            Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg).statusBarsPadding()) {
                body(Modifier.weight(1f).fillMaxWidth(), true)
                TeddBottomNav(active = Section.Card, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
            }
        } else {
            SettingsDrawerHost(drawer, onNavigate = onNavigate) {
                Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
                    TeddNavRail(active = Section.Card, onNavigate = onNavigate, onSettingsClick = drawer::open)
                    body(Modifier.weight(1f).fillMaxSize(), false)
                }
            }
        }
    }
}

@Composable
private fun CardContent(
    card: BusinessCard,
    onChange: (BusinessCard) -> Unit,
    dirty: Boolean,
    newerArrived: Boolean,
    onSave: () -> Unit,
    onCancel: () -> Unit,
    snackbar: TeddSnackbarState,
    compact: Boolean,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = if (compact) TeddSpace.S4 else TeddSpace.S8, vertical = if (compact) TeddSpace.S4 else TeddSpace.S6),
        verticalArrangement = Arrangement.spacedBy(if (compact) TeddSpace.S4 else TeddSpace.S6),
    ) {
        TeddScreenHeader(
            title = "Carte de visite",
            subtitle = "Montre-la, fais scanner le code, ou envoie-la. Modifie-la ci-dessous, puis enregistre.",
        )
        val picture = rememberGraphicsLayer()
        if (compact) {
            CardPreview(card, compact = true, picture = picture)
            ShareActions(card, snackbar, picture, Modifier.fillMaxWidth())
            CardForm(card, onChange, dirty, newerArrived, onSave, onCancel)
            // Le bouton flottant n'existe pas ici, mais la dernière ligne doit rester lisible au-dessus de la barre.
            Box(Modifier.height(TeddSpace.S4))
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S6), verticalAlignment = Alignment.Top) {
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
                    CardPreview(card, compact = false, picture = picture)
                    ShareActions(card, snackbar, picture)
                }
                CardForm(card, onChange, dirty, newerArrived, onSave, onCancel, Modifier.width(400.dp))
            }
        }
    }
}

/** La carte telle qu'on la montre : identité, coordonnées, code QR à scanner. */
@Composable
private fun CardPreview(card: BusinessCard, compact: Boolean, picture: GraphicsLayer) {
    val qr = remember(card) { if (card.isEmpty) null else QrCode.encode(card.toVCard()) }
    val backdrop = TeddTokens.Bg
    TeddCard(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            // La carte est aussi dessinée dans un calque, sur un fond plein et avec une marge : l'image partagée.
            .drawWithContent {
                val margin = 16.dp.toPx()
                picture.record(IntSize((size.width + 2 * margin).toInt(), (size.height + 2 * margin).toInt())) {
                    drawRect(backdrop)
                    translate(margin, margin) { this@drawWithContent.drawContent() }
                }
                drawContent()
            },
        padding = PaddingValues(TeddSpace.S6)) {
        if (card.isEmpty) {
            Text("Ta carte est vide", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                "Remplis au moins ton nom : la carte et son code QR apparaissent ici.",
                style = TeddType.Body,
                color = TeddTokens.InkMuted,
                modifier = Modifier.padding(top = TeddSpace.S1),
            )
            return@TeddCard
        }
        val identity: @Composable () -> Unit = {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                org.teddapp.project.ui.TeddFitText(card.name.trim().ifEmpty { "Sans nom" }, style = TeddType.Title1, minFontSize = 18.sp, maxLines = 2)
                if (card.headline.isNotEmpty()) Text(card.headline, style = TeddType.BodyStrong, color = TeddTokens.InkMuted)
                if (card.tagline.isNotBlank()) Text(card.tagline.trim(), style = TeddType.Body, color = TeddTokens.InkMuted)
            }
        }
        val contacts: @Composable () -> Unit = {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                ContactLine("phone", card.phone)
                ContactLine("mail", card.email)
                ContactLine("globe", card.website)
                ContactLine("pin", card.location)
                card.linkList.forEach { ContactLine("link", it) }
            }
        }
        val code: @Composable (Dp) -> Unit = { side ->
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                if (qr != null) {
                    QrImage(qr, side)
                    Text("Scanne pour m'ajouter à tes contacts", style = TeddType.Caption, color = TeddTokens.InkSubtle)
                } else {
                    Text(
                        "La carte est trop longue pour un code QR. Raccourcis un champ.",
                        style = TeddType.Caption,
                        color = TeddTokens.InkMuted,
                        modifier = Modifier.width(side),
                    )
                }
            }
        }
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
                identity()
                Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
                contacts()
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) { code(200.dp) }
            }
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S6), verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
                    identity()
                    Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
                    contacts()
                }
                code(176.dp)
            }
        }
    }
}

@Composable
private fun ContactLine(icon: String, value: String) {
    if (value.isBlank()) return
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        TeddIcon(name = icon, size = 16.dp, color = TeddTokens.InkMuted)
        Text(value.trim(), style = TeddType.Body, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
    }
}

/**
 * Le code QR, toujours noir sur blanc quel que soit le thème : c'est ce que les
 * lecteurs attendent. La marge blanche de quatre modules fait partie du code.
 */
@Composable
private fun QrImage(modules: Array<BooleanArray>, side: Dp) {
    Canvas(modifier = Modifier.size(side).background(Color.White, RoundedCornerShape(TeddTokens.RSm))) {
        val cell = size.minDimension / (modules.size + 8)
        for (y in modules.indices) for (x in modules.indices) {
            if (modules[y][x]) {
                // Un rien de recouvrement évite les filets blancs entre deux modules.
                drawRect(Color.Black, topLeft = Offset((x + 4) * cell, (y + 4) * cell), size = Size(cell + 0.5f, cell + 0.5f))
            }
        }
    }
}

@Composable
private fun ShareActions(card: BusinessCard, snackbar: TeddSnackbarState, picture: GraphicsLayer, modifier: Modifier = Modifier) {
    val clipboard = LocalClipboardManager.current
    val scope = rememberCoroutineScope()
    val shareText = TeddShare.shareText
    val shareImage = TeddShare.shareImage
    // Sur téléphone, les boutons se partagent la largeur ; sur ordinateur, ils gardent leur taille.
    Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        val each = if (shareText != null) Modifier.weight(1f) else Modifier
        if (shareImage != null) {
            TeddButton(
                text = if (TeddShare.imageIsSaved) "Exporter l'image" else "Image",
                onClick = {
                    scope.launch {
                        runCatching { shareImage("carte-de-visite.png", picture.toImageBitmap()) }
                            .onSuccess { message -> message?.let(snackbar::show) }
                            .onFailure { snackbar.show("L'image n'a pas pu être créée") }
                    }
                },
                kind = TeddButtonKind.Primary,
                icon = "share",
                enabled = !card.isEmpty,
                modifier = each,
            )
        }
        if (shareText != null) {
            TeddButton(
                text = "Texte",
                onClick = { shareText("Carte de visite", card.toShareText()) },
                icon = "send",
                enabled = !card.isEmpty,
                modifier = each,
            )
        }
        TeddButton(
            text = if (shareText != null) "Copier" else "Copier le texte",
            onClick = {
                clipboard.setText(AnnotatedString(card.toShareText()))
                snackbar.show("Carte copiée")
            },
            kind = if (shareImage == null) TeddButtonKind.Primary else TeddButtonKind.Secondary,
            icon = "copy",
            enabled = !card.isEmpty,
            modifier = each,
        )
    }
}

@Composable
private fun CardForm(
    card: BusinessCard,
    onChange: (BusinessCard) -> Unit,
    dirty: Boolean,
    newerArrived: Boolean,
    onSave: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier,
) {
    TeddCard(modifier = modifier.fillMaxWidth(), title = "Tes informations") {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            TeddField("Nom", card.name, { onChange(card.copy(name = it)) }, placeholder = "Prénom et nom")
            TeddField("Titre", card.title, { onChange(card.copy(title = it)) }, placeholder = "Ce que tu fais")
            TeddField("Organisation", card.organization, { onChange(card.copy(organization = it)) }, placeholder = "Entreprise, école… (optionnel)")
            TeddField("Phrase d'accroche", card.tagline, { onChange(card.copy(tagline = it)) }, placeholder = "Une ligne sur toi (optionnel)")
            TeddField("Téléphone", card.phone, { onChange(card.copy(phone = it)) }, keyboardType = KeyboardType.Phone, placeholder = "+223 …")
            TeddField("E-mail", card.email, { onChange(card.copy(email = it)) }, keyboardType = KeyboardType.Email, placeholder = "toi@exemple.org")
            TeddField("Site web", card.website, { onChange(card.copy(website = it)) }, keyboardType = KeyboardType.Uri, placeholder = "https://…")
            TeddField("Ville", card.location, { onChange(card.copy(location = it)) }, placeholder = "Ville, pays")
            TeddField("Autres liens", card.links, { onChange(card.copy(links = it)) }, keyboardType = KeyboardType.Uri, placeholder = "LinkedIn, GitHub… séparés par une virgule")
            if (newerArrived) {
                Text(
                    "Une version plus récente est arrivée d'un autre appareil. Enregistrer la remplacera.",
                    style = TeddType.Caption,
                    color = TeddTokens.InkMuted,
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), modifier = Modifier.padding(top = TeddSpace.S1)) {
                TeddButton(text = "Enregistrer", onClick = onSave, kind = TeddButtonKind.Primary, icon = "check", enabled = dirty)
                TeddButton(text = "Annuler", onClick = onCancel, kind = TeddButtonKind.Ghost, enabled = dirty)
            }
        }
    }
}
